import { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { useNavigate } from '@tanstack/react-router';
import { useSession } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SidebarProvider, SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from '@/components/app-sidebar';
import { ModeToggle } from '@/components/mode-toggle';
import {
  RiHistoryLine,
  RiCheckLine,
  RiCloseLine,
  RiTimeLine,
  RiDatabase2Line,
  RiPlayLine,
  RiLoader2Line,
} from '@remixicon/react';
import { api, QueryHistory } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

export function HistoryPage() {
  const { data: session } = useSession();
  const navigate = useNavigate();
  const [history, setHistory] = useState<QueryHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedQuery, setSelectedQuery] = useState<QueryHistory | null>(null);

  useEffect(() => {
    if (session?.user?.id) {
      loadHistory();
    }
  }, [session?.user?.id]);

  const loadHistory = async () => {
    if (!session?.user?.id) return;

    try {
      setIsLoading(true);
      const result = await api.getQueryHistory(session.user.id);
      setHistory(result.history);
    } catch (error: any) {
      toast.error('Failed to load query history');
    } finally {
      setIsLoading(false);
    }
  };

  const runQuery = (item: QueryHistory) => {
    navigate({ to: `/query/${item.databaseId}` });
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="sticky top-0 flex h-16 shrink-0 items-center gap-2 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 z-10">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="h-4 mr-2" />
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>Query History</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          <div className="ml-auto">
            <ModeToggle />
          </div>
        </header>

        <main className="flex-1 p-6 overflow-auto md:p-8 lg:p-10">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mb-8"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center justify-center size-12 rounded-xl bg-primary/10">
                  <RiHistoryLine className="size-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold">Query History</h1>
                  <p className="text-muted-foreground">View all your executed queries</p>
                </div>
              </div>
            </motion.div>

            {/* History List */}
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RiLoader2Line className="size-8 animate-spin text-primary" />
              </div>
            ) : history.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-12"
              >
                <RiHistoryLine className="size-16 mx-auto mb-4 text-muted-foreground/20" />
                <h3 className="text-lg font-semibold mb-2">No query history yet</h3>
                <p className="text-muted-foreground mb-6">
                  Execute some queries to see them here
                </p>
                <Button onClick={() => navigate({ to: '/databases' })}>
                  <RiDatabase2Line className="size-4 mr-2" />
                  Go to Databases
                </Button>
              </motion.div>
            ) : (
              <ScrollArea className="h-[calc(100vh-16rem)]">
                <div className="space-y-3">
                  {history.map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card
                        className="group cursor-pointer hover:border-primary/50 transition-all duration-300 hover:shadow-md"
                        onClick={() => setSelectedQuery(item)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-4">
                            {/* Status Icon */}
                            <div
                              className={`flex items-center justify-center size-10 rounded-lg flex-shrink-0 ${
                                item.status === 'success'
                                  ? 'bg-green-500/10'
                                  : 'bg-red-500/10'
                              }`}
                            >
                              {item.status === 'success' ? (
                                <RiCheckLine className="size-5 text-green-600 dark:text-green-400" />
                              ) : (
                                <RiCloseLine className="size-5 text-red-600 dark:text-red-400" />
                              )}
                            </div>

                            {/* Query Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge
                                  variant={item.status === 'success' ? 'default' : 'destructive'}
                                  className="text-xs"
                                >
                                  {item.status}
                                </Badge>
                                <span className="text-xs text-muted-foreground">
                                  {formatDistanceToNow(new Date(item.createdAt), {
                                    addSuffix: true,
                                  })}
                                </span>
                              </div>
                              <code className="block text-sm font-mono bg-muted/50 px-3 py-2 rounded-md truncate">
                                {item.query}
                              </code>
                              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <RiTimeLine className="size-3" />
                                  {item.executionTime}ms
                                </span>
                                <span>{item.rowCount} rows</span>
                              </div>
                              {item.error && (
                                <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                                  {item.error}
                                </p>
                              )}
                            </div>

                            {/* Action Button */}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => {
                                e.stopPropagation();
                                runQuery(item);
                              }}
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <RiPlayLine className="size-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
